import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaPaw, FaHeartbeat, FaUserMd, FaSyringe, FaStethoscope, FaCut, FaTooth, FaMicroscope, FaHospital, FaPhone, FaWhatsapp, FaCalendarAlt, FaClinicMedical, FaInstagram } from 'react-icons/fa';
import { motion } from 'framer-motion';
import IconWrapper from '../components/IconWrapper';

// Animasyon için varyantlar
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: "easeOut"
    }
  })
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const Home: React.FC = () => {
  const [formData, setFormData] = useState({
    petOwnerName: '',
    petName: '',
    petType: 'dog',
    phone: '',
    preferredDate: '',
    preferredTime: '',
    description: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Burada form verilerini işleyebilirsiniz
    console.log('Form data:', formData);
    // Form gönderildikten sonra formu sıfırla
    setFormData({
      petOwnerName: '',
      petName: '',
      petType: 'dog',
      phone: '',
      preferredDate: '',
      preferredTime: '',
      description: ''
    });
    // Başarılı mesajı göster
    alert('Randevu talebiniz alınmıştır. En kısa sürede size dönüş yapacağız.');
  };

  const quickActions = [
    {
      icon: FaPhone,
      text: "Hemen Arayın",
      action: "tel:+905533077616",
    },
    {
      icon: FaWhatsapp,
      text: "WhatsApp",
      action: "https://wa.me/905533077616",
    },
    {
      icon: FaInstagram,
      text: "Instagram",
      action: "https://instagram.com/yahsiveterinerklinigi",
    },
    {
      icon: FaClinicMedical,
      text: "Adres",
      description: "Demirtaş Cumhuriyet mahallesi 1205 sokak, 16000 Osmangazi/Bursa",
      action: "/iletisim",
    }
  ];

  const services = [
    {
      icon: FaUserMd,
      title: "Genel Muayene",
      description: "Kapsamlı sağlık kontrolü ve aşılama hizmetleri"
    },
    {
      icon: FaClinicMedical,
      title: "Cerrahi Operasyonlar",
      description: "Modern ameliyathane ve uzman ekip"
    },
    {
      icon: FaPaw,
      title: "Yatılı Bakım",
      description: "Güvenli ve konforlu konaklama imkanı"
    }
  ];

  // Yorumlar
  const testimonials = [
    {
      name: "Ayşe Y.",
      text: "Kedimiz Minnuş'un sağlık problemleri için her zaman ilgili ve sabırlı davrandılar. Artık başka bir veteriner düşünemiyoruz bile.",
      petName: "Minnuş",
      petType: "Kedi"
    },
    {
      name: "Mehmet K.",
      text: "Köpeğimiz Paşa'nın ameliyatı sırasında gösterdikleri profesyonellik ve ameliyat sonrası takip sürecindeki ilgileri için minnettarız.",
      petName: "Paşa",
      petType: "Köpek"
    },
    {
      name: "Zeynep A.",
      text: "Tavşanımız için burayı tercih ettik ve çok memnun kaldık. Küçük dostlarımız için de aynı özeni gösteriyorlar.",
      petName: "Zıpzıp",
      petType: "Tavşan"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary via-primary-dark to-secondary min-h-[80vh] flex items-center text-white py-20 overflow-hidden">
        {/* Dekoratif arka plan elementleri */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-96 h-96 -top-48 -right-48 bg-white/10 rounded-full blur-xl"></div>
          <div className="absolute w-96 h-96 -bottom-48 -left-48 bg-secondary/20 rounded-full blur-xl"></div>
          <div className="absolute w-72 h-72 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-primary-light/10 rounded-full blur-lg"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
            <motion.div 
              className="lg:w-1/2 text-center lg:text-left space-y-6"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold leading-tight">
                Evcil Dostlarınız İçin{" "}
                <span className="text-secondary bg-white/10 px-4 py-2 rounded-lg inline-block mt-2">
                  En İyi Bakım
                </span>
              </h1>
              <p className="text-lg md:text-xl text-blue-100 max-w-xl">
                Profesyonel veteriner ekibimizle can dostlarınızın sağlığı için 7/24 yanınızdayız
              </p>
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <Link 
                  to="/randevu" 
                  className="bg-white text-primary hover:bg-secondary hover:text-white px-8 py-3 rounded-full font-medium transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl text-lg flex items-center gap-2"
                >
                  <FaCalendarAlt />
                  Randevu Al
                </Link>
                <Link 
                  to="/hizmetler" 
                  className="bg-secondary/90 text-white hover:bg-secondary px-8 py-3 rounded-full font-medium transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl text-lg flex items-center gap-2"
                >
                  <FaPaw />
                  Hizmetlerimiz
                </Link>
              </div>
            </motion.div>
            
            <motion.div 
              className="lg:w-1/2"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="relative w-2/3 mx-auto">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-3xl transform rotate-6"></div>
                <img 
                  src="https://images.unsplash.com/photo-1587764379873-97837921fd44?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                  alt="Sevimli köpek" 
                  className="rounded-3xl shadow-2xl max-w-full h-auto relative z-10 transform hover:scale-105 transition-transform duration-300"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Neden Biz Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-800 mb-4">
              Neden Bizi Tercih Etmelisiniz?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Yahşi Veteriner Kliniği olarak tüm can dostlarınıza profesyonel ve sevgi dolu hizmet sunuyoruz
            </p>
          </motion.div>
          
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div 
              className="bg-white p-8 rounded-2xl shadow-lg text-center transform transition-all duration-300 hover:shadow-xl hover:-translate-y-2"
              variants={fadeIn}
              custom={0}
            >
              <div className="bg-primary-light/20 w-20 h-20 flex items-center justify-center rounded-full mx-auto mb-6">
                <IconWrapper icon={FaPaw} className="text-4xl text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Uzman Kadro</h3>
              <p className="text-gray-600">
                Alanında uzmanlaşmış veteriner hekimlerimiz ve teknisyenlerimizle kaliteli sağlık hizmeti sunuyoruz.
              </p>
            </motion.div>
            
            <motion.div 
              className="bg-white p-8 rounded-2xl shadow-lg text-center transform transition-all duration-300 hover:shadow-xl hover:-translate-y-2"
              variants={fadeIn}
              custom={1}
            >
              <div className="bg-secondary-light/20 w-20 h-20 flex items-center justify-center rounded-full mx-auto mb-6">
                <IconWrapper icon={FaHeartbeat} className="text-4xl text-secondary" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Modern Ekipman</h3>
              <p className="text-gray-600">
                En son teknoloji ile donatılmış kliniğimizde, en doğru teşhis ve tedavi yöntemlerini uyguluyoruz.
              </p>
            </motion.div>
            
            <motion.div 
              className="bg-white p-8 rounded-2xl shadow-lg text-center transform transition-all duration-300 hover:shadow-xl hover:-translate-y-2"
              variants={fadeIn}
              custom={2}
            >
              <div className="bg-accent-light/20 w-20 h-20 flex items-center justify-center rounded-full mx-auto mb-6">
                <IconWrapper icon={FaUserMd} className="text-4xl text-accent" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">7/24 Hizmet</h3>
              <p className="text-gray-600">
                Acil durumlar için her zaman yanınızdayız. Her zaman aramalara açığız.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Hizmetler Önizleme */}
      <section className="py-20 bg-gray-100">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-800 mb-4">
              Hizmetlerimiz
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Evcil dostlarınıza sunduğumuz kapsamlı veteriner hizmetleri
            </p>
          </motion.div>
          
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {services.map((service, index) => (
              <motion.div 
                key={index}
                className="bg-white rounded-xl shadow-md overflow-hidden transform transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                variants={fadeIn}
                custom={index}
              >
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className={`w-12 h-12 flex items-center justify-center rounded-full ${index % 3 === 0 ? 'bg-primary-light/20 text-primary' : index % 3 === 1 ? 'bg-secondary-light/20 text-secondary' : 'bg-accent-light/20 text-accent'}`}>
                      <IconWrapper icon={service.icon} className="text-2xl" />
                    </div>
                    <h3 className="ml-4 text-lg font-semibold text-gray-800">{service.title}</h3>
                  </div>
                  <p className="text-gray-600">{service.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
          
          <div className="text-center mt-12">
            <Link 
              to="/hizmetler" 
              className="inline-block bg-primary text-white hover:bg-primary-dark px-8 py-3 rounded-full font-semibold transition-all duration-300 shadow-md hover:shadow-lg"
            >
              Tüm Hizmetlerimiz
            </Link>
          </div>
        </div>
      </section>

      {/* Müşteri Yorumları */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-primary opacity-5 z-0"></div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-800 mb-4">
              Mutlu Müşterilerimiz
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Can dostlarımızın sahiplerinden gelen değerli yorumlar
            </p>
          </motion.div>
          
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {testimonials.map((testimonial, index) => (
              <motion.div 
                key={index}
                className="bg-white p-8 rounded-2xl shadow-md relative"
                variants={fadeIn}
                custom={index}
              >
                <div className="text-primary opacity-20 text-8xl absolute top-4 right-6 z-0">"</div>
                <div className="relative z-10">
                  <p className="text-gray-700 mb-6 italic">{testimonial.text}</p>
                  <div className="flex items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${index % 3 === 0 ? 'bg-primary-light' : index % 3 === 1 ? 'bg-secondary-light' : 'bg-accent-light'}`}>
                      <span className="text-white font-bold">{testimonial.name.charAt(0)}</span>
                    </div>
                    <div className="ml-4">
                      <h4 className="font-semibold text-gray-800">{testimonial.name}</h4>
                      <p className="text-sm text-gray-500">{testimonial.petName} - {testimonial.petType}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Hızlı İletişim Section */}
      <section className="py-16 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {quickActions.map((action, index) => (
              <motion.a
                key={index}
                href={action.action}
                target={action.action.startsWith('http') ? '_blank' : undefined}
                rel={action.action.startsWith('http') ? 'noopener noreferrer' : undefined}
                className="bg-white rounded-2xl shadow-md p-8 flex flex-col items-center text-center hover:shadow-lg transition-shadow duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
              >
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                  action.icon === FaInstagram ? 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500' :
                  action.icon === FaWhatsapp ? 'bg-green-500' :
                  'bg-primary'
                }`}>
                  <IconWrapper icon={action.icon} className="text-3xl text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800">{action.text}</h3>
                {action.description && (
                  <p className="text-gray-600 mt-2 text-sm">{action.description}</p>
                )}
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-gradient-to-r from-secondary to-secondary-dark text-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="md:w-2/3 mb-6 md:mb-0 text-center md:text-left">
              <h2 className="text-2xl md:text-3xl font-display font-bold mb-2">
                Evcil dostunuz için randevu alın
              </h2>
              <p className="text-lg text-yellow-100">
                Profesyonel ekibimizle dostunuzun sağlığı için buradayız
              </p>
            </div>
            <div>
              <Link 
                to="/randevu" 
                className="inline-block bg-white text-secondary hover:bg-yellow-100 px-6 py-3 rounded-full font-bold text-base transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Hemen Randevu Al
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home; 